import { FC, ReactNode, useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import * as Enums from '@/utils/enums';
import PS from '@/services/permission/permission-service';
import Storage from '@/utils/storage';
import constants from '@/utils/constants';
import { pagePermissions, lockedWithoutAccessPermissions } from '@/utils/auth';

interface AuthProviderProps {
  children: ReactNode;
}

const getRequiredPermissions = (pathname: string): string[] | null => {
  if (pagePermissions[pathname] !== undefined) return pagePermissions[pathname];

  const parts = pathname.split('/').filter(Boolean);
  for (let i = parts.length - 1; i > 0; i--) {
    const attempt = '/' + parts.slice(0, i).join('/');
    if (pagePermissions[attempt] !== undefined) return pagePermissions[attempt];
  }

  return null;
};

const isPublicRoute = (pathname: string): boolean => {
  const lower = pathname.toLowerCase();
  if (constants.PUBLIC_ROUTES_EXACT.has(lower)) return true;
  return constants.PUBLIC_ROUTE_PREFIXES.some((prefix: any) => lower.startsWith(prefix));
};

const AuthProvider: FC<AuthProviderProps> = ({ children }: any) => {
  const router = useRouter();
  const pathname = router.pathname;

  const requiredPermissions = getRequiredPermissions(pathname);
  const usesPermissions = Array.isArray(requiredPermissions) && requiredPermissions.length > 0;
  const permissionsInError = requiredPermissions === null ? false : false; // null means no entry found (no restriction)

  const [authorized, setAuthorized] = useState(!usesPermissions);

  useEffect(() => {
    if (isPublicRoute(pathname)) {
      setAuthorized(true);
      return;
    }

    // Force HTTPS in production
    if (
      window.location.href.indexOf('http:') > -1 &&
      window.location.href.indexOf('localhost') < 0
    ) {
      window.location.href = window.location.href.replace('http:', 'https:');
      return;
    }

    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'logout') {
        Router.push('/login');
      }
    };

    window.addEventListener('storage', syncLogout);

    if (usesPermissions) {
      let { permissions, subscriptionInfo, hasVan } = {
        permissions: PS.getPermissions(),
        subscriptionInfo: Storage.getCookie(Enums.Cookie.subscriptionInfo),
        hasVan: Storage.getCookie(Enums.Cookie.hasVan),
      };

      if (
        Storage.getCookie('bypassLockout') === 'true' &&
        subscriptionInfo?.AccessStatus === Enums.AccessStatus.LockedWithOutAccess
      ) {
        subscriptionInfo = {
          ...subscriptionInfo,
          AccessStatus: Enums.AccessStatus.Live,
        };
      }

      const permissionsHasSysAdmin = permissions?.includes(Enums.PermissionName.MasterSystemAdmin);
      const lockedWithOutAccess =
        subscriptionInfo?.AccessStatus === Enums.AccessStatus.LockedWithOutAccess;

      const allowedPermissions = lockedWithOutAccess
        ? lockedWithoutAccessPermissions
            .map((x: any) => x.value)
            .filter((x: any) => permissions.includes(x))
        : permissions;

      if (permissionsHasSysAdmin && lockedWithOutAccess) {
        allowedPermissions.push(Enums.PermissionName.MasterSystemAdmin);
      }

      if (hasVan && !allowedPermissions.includes(Enums.PermissionName.VanManage)) {
        allowedPermissions.push(Enums.PermissionName.VanManage);
      }

      const authorised = PS.hasPermission(requiredPermissions, false, allowedPermissions);

      if (!authorised) {
        alert('You do not have access to this page.');
        if (window.history.state?.url?.startsWith('/login')) {
          Router.replace('/', '/');
        } else {
          Router.replace('/');
        }
      } else {
        setAuthorized(true);
      }
    }

    return () => {
      window.removeEventListener('storage', syncLogout);
      window.localStorage.removeItem('logout');
    };
  }, [pathname]);

  if (!authorized) return null;

  return <>{children}</>;
};

export default AuthProvider;
